/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// SafeCircle Live Activity — pure, framework-agnostic core for the motion
// "live state" feature. Holds the normalized state vocabulary, the
// platform-input → normalized-state mappers, and the debounce predicate.
//
// The native iOS (CMMotionActivity) and Android (DetectedActivity) plugins
// emit these SAME normalized strings over the `activityChange` event, so the
// TS layer never has to branch on platform. The mappers below mirror the
// native mapping tables one-for-one; they exist in TS so the contract can be
// unit-tested in Vitest without a physical device (a simulator/emulator can't
// generate real motion activity).

/** Normalized motion state shared across iOS, Android, and the TS layer. */
export type ActivityState =
  | "still"
  | "in_vehicle"
  | "walking"
  | "running"
  | "on_bicycle"
  | "unknown";

/** Normalized confidence bucket. iOS CMMotionActivityConfidence and Android's
 *  per-activity confidence both collapse into these three. */
export type ActivityConfidence = "low" | "medium" | "high";

/** The shape the native `activityChange` event carries, and the shape the
 *  write layer persists (minus the server timestamp). */
export interface ActivitySample {
  state: ActivityState;
  confidence: ActivityConfidence;
}

export const ALL_ACTIVITY_STATES: readonly ActivityState[] = [
  "still",
  "in_vehicle",
  "walking",
  "running",
  "on_bicycle",
  "unknown",
] as const;

/**
 * iOS CMMotionActivity has independent booleans (stationary/automotive/
 * walking/running/cycling/unknown), more than one of which can be true. The
 * native plugin resolves a single winner before emitting, but we accept the
 * raw boolean bag here so the mapper can BE the resolver and stay testable.
 * Priority when multiple are set: running > walking > cycling > automotive >
 * stationary (most-specific, highest-energy motion wins). `unknown` only when
 * nothing else is set.
 */
export interface IosActivityFlags {
  stationary?: boolean;
  automotive?: boolean;
  walking?: boolean;
  running?: boolean;
  cycling?: boolean;
}

export function mapIosActivity(flags: IosActivityFlags): ActivityState {
  if (flags.running) return "running";
  if (flags.walking) return "walking";
  if (flags.cycling) return "on_bicycle";
  if (flags.automotive) return "in_vehicle";
  if (flags.stationary) return "still";
  return "unknown";
}

/**
 * Android DetectedActivity int constants (from
 * com.google.android.gms.location.DetectedActivity). We accept the int so the
 * mapper mirrors the native `when (type)` exactly. TILTING (5) and any
 * unrecognized value normalize to `unknown`.
 */
export const ANDROID_DETECTED_ACTIVITY = {
  IN_VEHICLE: 0,
  ON_BICYCLE: 1,
  ON_FOOT: 2,
  STILL: 3,
  UNKNOWN: 4,
  TILTING: 5,
  WALKING: 7,
  RUNNING: 8,
} as const;

export function mapAndroidActivity(type: number): ActivityState {
  switch (type) {
    case ANDROID_DETECTED_ACTIVITY.STILL:
      return "still";
    case ANDROID_DETECTED_ACTIVITY.IN_VEHICLE:
      return "in_vehicle";
    case ANDROID_DETECTED_ACTIVITY.ON_FOOT:
    case ANDROID_DETECTED_ACTIVITY.WALKING:
      return "walking";
    case ANDROID_DETECTED_ACTIVITY.RUNNING:
      return "running";
    case ANDROID_DETECTED_ACTIVITY.ON_BICYCLE:
      return "on_bicycle";
    // TILTING, UNKNOWN, and anything unexpected → unknown.
    default:
      return "unknown";
  }
}

/** The persisted/streamed activity doc as read by a SafeCircle viewer. */
export interface FriendActivity {
  /** True only when a valid doc with sharingEnabled === true was read. */
  shared: boolean;
  state: ActivityState;
  confidence: ActivityConfidence;
  /** Epoch ms of the last update, or null when not shared/available. */
  updatedAt: number | null;
}

export const NOT_SHARED_ACTIVITY: FriendActivity = {
  shared: false,
  state: "unknown",
  confidence: "low",
  updatedAt: null,
};

/**
 * Parse a raw /users/{uid}/liveState/activity snapshot value into a safe
 * FriendActivity. Anything that isn't an explicitly-shared, known, non-unknown
 * state collapses to NOT_SHARED_ACTIVITY so the UI shows a neutral "not
 * shared" state and never leaks a partial/garbage value.
 */
export function parseFriendActivity(raw: unknown): FriendActivity {
  if (!raw || typeof raw !== "object") return NOT_SHARED_ACTIVITY;
  const r = raw as Record<string, unknown>;
  if (r.sharingEnabled !== true) return NOT_SHARED_ACTIVITY;
  const state = ALL_ACTIVITY_STATES.includes(r.state as ActivityState)
    ? (r.state as ActivityState)
    : "unknown";
  if (state === "unknown") return NOT_SHARED_ACTIVITY;
  const confidence: ActivityConfidence =
    r.confidence === "high" || r.confidence === "medium"
      ? (r.confidence as ActivityConfidence)
      : "low";
  return {
    shared: true,
    state,
    confidence,
    updatedAt: typeof r.updatedAt === "number" ? r.updatedAt : null,
  };
}

/** Default minimum gap between writes. Privacy + cost: we never stream. */
export const ACTIVITY_DEBOUNCE_MS = 60_000;

/**
 * Gate for the RTDB write of a LIVE sample. Returns true only when ALL of:
 *  - the next state is a real, broadcastable state (NOT `unknown` — we never
 *    publish `unknown` as a live state; the terminal unknown-on-disable doc is
 *    written separately, outside this gate), and
 *  - the normalized state actually CHANGED from what we last wrote, and
 *  - at least `debounceMs` has elapsed since the last write.
 *
 * `prev` / `lastWriteAtMs` are null before the first write — the first real
 * (non-unknown) sample always writes. Passing the same state twice never
 * writes again, so a chatty native emitter can't spam RTDB, and the initial
 * `unknown` that the hook reports before any native event never gets written.
 */
export function shouldWriteActivity(
  prev: ActivityState | null,
  next: ActivityState,
  lastWriteAtMs: number | null,
  nowMs: number,
  debounceMs: number = ACTIVITY_DEBOUNCE_MS,
): boolean {
  if (next === "unknown") return false;
  if (next === prev) return false;
  if (lastWriteAtMs === null) return true;
  return nowMs - lastWriteAtMs >= debounceMs;
}
